/**
 * HubSpotStageMappingPanel
 *
 * Lets the user map their HubSpot deal pipeline stages
 * to standardized conversion types (lead, booked_call, sale, etc.)
 * and then trigger a sync to pull those deals into the tracking dashboard.
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Loader2, RefreshCw, Save, Check, AlertCircle } from "lucide-react";

// ─── Constants ───────────────────────────────────────────────────────────────

const CONVERSION_TYPE_OPTIONS = [
  { value: "", labelKey: "hubspotMapping.conversionType.ignore" },
  { value: "lead", labelKey: "hubspotMapping.conversionType.lead" },
  { value: "qualified_lead", labelKey: "hubspotMapping.conversionType.qualified_lead" },
  { value: "booked_call", labelKey: "hubspotMapping.conversionType.booked_call" },
  { value: "no_show", labelKey: "hubspotMapping.conversionType.no_show" },
  { value: "unqualified_call", labelKey: "hubspotMapping.conversionType.unqualified_call" },
  { value: "sale", labelKey: "hubspotMapping.conversionType.sale" },
] as const;

// ─── Types ───────────────────────────────────────────────────────────────────

interface PipelineStage {
  id: string;
  label: string;
  displayOrder: number;
}

interface Pipeline {
  id: string;
  label: string;
  stages: PipelineStage[];
}

interface StageMapping {
  pipelineId: string;
  pipelineLabel: string;
  stageId: string;
  stageLabel: string;
  conversionType: string | null;
  revenueField: string | null;
}

interface SyncResult {
  dealsScanned: number;
  dealsSkippedNoMapping: number;
  dealsSkippedAlreadySynced: number;
  dealsSkippedNoContact: number;
  conversionsCreated: number;
  errors: string[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function thirtyDaysAgo(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function HubspotStageMappingPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [mappingState, setMappingState] = useState<Record<string, string>>({}); // stageId → conversionType
  const [revenueState, setRevenueState] = useState<Record<string, string>>({}); // stageId → revenueField
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [syncDateFrom, setSyncDateFrom] = useState(thirtyDaysAgo());
  const [syncDateTo, setSyncDateTo] = useState(today());
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);

  // Load pipelines from HubSpot API
  const pipelinesQuery = useQuery<{ pipelines: Pipeline[] }>({
    queryKey: ["hubspot-pipelines"],
    queryFn: () => apiRequest("GET", "/api/integrations/hubspot/pipelines"),
    staleTime: 5 * 60_000,
  });

  // Load saved stage mappings
  const mappingsQuery = useQuery<{ mappings: StageMapping[] }>({
    queryKey: ["hubspot-stage-mappings"],
    queryFn: () => apiRequest("GET", "/api/integrations/hubspot/stage-mappings"),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!mappingsQuery.data) return;
    const ct: Record<string, string> = {};
    const rv: Record<string, string> = {};
    for (const m of mappingsQuery.data.mappings) {
      ct[m.stageId] = m.conversionType ?? "";
      rv[m.stageId] = m.revenueField ?? "";
    }
    setMappingState(ct);
    setRevenueState(rv);
  }, [mappingsQuery.data]);

  const syncMutation = useMutation<SyncResult, Error, void>({
    mutationFn: () =>
      apiRequest("POST", "/api/integrations/hubspot/sync", {
        dateFrom: syncDateFrom,
        dateTo: syncDateTo,
      }),
    onSuccess: (data) => {
      setSyncResult(data);
      queryClient.invalidateQueries({ queryKey: ["tracking-conversions"] });
    },
  });

  const isLoading = pipelinesQuery.isLoading || mappingsQuery.isLoading;
  const pipelines = pipelinesQuery.data?.pipelines ?? [];

  // ── Build payload and save ──
  const handleSave = async () => {
    setSaveStatus("saving");
    try {
      const payload: StageMapping[] = [];
      for (const pipeline of pipelines) {
        for (const stage of pipeline.stages) {
          payload.push({
            pipelineId: pipeline.id,
            pipelineLabel: pipeline.label,
            stageId: stage.id,
            stageLabel: stage.label,
            conversionType: mappingState[stage.id] || null,
            revenueField: revenueState[stage.id] || null,
          });
        }
      }
      await apiRequest("PUT", "/api/integrations/hubspot/stage-mappings", { mappings: payload });
      queryClient.invalidateQueries({ queryKey: ["hubspot-stage-mappings"] });
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2500);
    } catch {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-3 text-xs text-white/40">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        {t("hubspotMapping.loading")}
      </div>
    );
  }

  if (pipelinesQuery.isError) {
    return (
      <div className="flex items-center gap-2 py-3 text-xs text-red-400/80">
        <AlertCircle className="w-3.5 h-3.5" />
        {t("hubspotMapping.loadError")}
      </div>
    );
  }

  return (
    <div className="space-y-4 mt-2">
      {/* ── Stage mapping table ── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium text-white/70">{t("hubspotMapping.tableTitle")}</p>
          <button
            onClick={() => pipelinesQuery.refetch()}
            className="text-white/30 hover:text-white/60 transition-colors"
            title={t("hubspotMapping.refreshPipeline")}
          >
            <RefreshCw className="w-3 h-3" />
          </button>
        </div>

        {pipelines.length === 0 ? (
          <p className="text-xs text-white/30 py-2">{t("hubspotMapping.noPipelines")}</p>
        ) : (
          pipelines.map((pipeline) => (
            <div key={pipeline.id} className="mb-4">
              <div className="text-[10px] font-mono text-white/40 uppercase tracking-wider mb-1.5 px-1">
                {pipeline.label}
              </div>
              <div className="rounded-lg border border-white/[0.08] overflow-hidden divide-y divide-white/[0.05]">
                {[...pipeline.stages]
                  .sort((a, b) => a.displayOrder - b.displayOrder)
                  .map((stage) => (
                    <div key={stage.id} className="flex items-center gap-3 px-3 py-2 bg-black/10">
                      <span className="text-xs text-white/60 flex-1 min-w-0 truncate">
                        {stage.label}
                      </span>
                      <select
                        value={mappingState[stage.id] ?? ""}
                        onChange={(e) =>
                          setMappingState((prev) => ({ ...prev, [stage.id]: e.target.value }))
                        }
                        className="text-xs bg-black/40 border border-white/[0.10] rounded px-2 py-1 text-white/80 focus:outline-none focus:border-white/20 flex-shrink-0"
                      >
                        {CONVERSION_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {t(opt.labelKey)}
                          </option>
                        ))}
                      </select>
                      {/* Show revenue field selector only for 'sale' mapping */}
                      {mappingState[stage.id] === "sale" && (
                        <select
                          value={revenueState[stage.id] ?? ""}
                          onChange={(e) =>
                            setRevenueState((prev) => ({ ...prev, [stage.id]: e.target.value }))
                          }
                          className="text-xs bg-black/40 border border-white/[0.10] rounded px-2 py-1 text-white/80 focus:outline-none focus:border-white/20 flex-shrink-0"
                          title={t("hubspotMapping.revenueField")}
                        >
                          <option value="">{t("hubspotMapping.noRevenue")}</option>
                          <option value="amount">{t("hubspotMapping.amount")}</option>
                        </select>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          ))
        )}

        {pipelines.length > 0 && (
          <button
            onClick={handleSave}
            disabled={saveStatus === "saving"}
            className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              saveStatus === "saved"
                ? "bg-emerald-500/20 border border-emerald-500/30 text-emerald-300"
                : saveStatus === "error"
                  ? "bg-red-500/20 border border-red-500/30 text-red-300"
                  : "bg-white/[0.06] border border-white/[0.10] text-white/70 hover:bg-white/[0.10]"
            }`}
          >
            {saveStatus === "saving" ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : saveStatus === "saved" ? (
              <Check className="w-3.5 h-3.5" />
            ) : (
              <Save className="w-3.5 h-3.5" />
            )}
            {saveStatus === "saved"
              ? t("hubspotMapping.save.saved")
              : saveStatus === "error"
                ? t("hubspotMapping.save.error")
                : t("hubspotMapping.save.button")}
          </button>
        )}
      </div>

      {/* ── Sync section ── */}
      <div className="border-t border-white/[0.06] pt-4 space-y-3">
        <p className="text-xs font-medium text-white/70">{t("hubspotMapping.sync.title")}</p>
        <p className="text-[11px] text-white/40 leading-relaxed">{t("hubspotMapping.sync.desc")}</p>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] text-white/40">{t("hubspotMapping.sync.from")}</label>
            <input
              type="date"
              value={syncDateFrom}
              onChange={(e) => setSyncDateFrom(e.target.value)}
              className="text-xs bg-black/40 border border-white/[0.10] rounded px-2 py-1 text-white/70 focus:outline-none focus:border-white/20"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] text-white/40">{t("hubspotMapping.sync.to")}</label>
            <input
              type="date"
              value={syncDateTo}
              onChange={(e) => setSyncDateTo(e.target.value)}
              className="text-xs bg-black/40 border border-white/[0.10] rounded px-2 py-1 text-white/70 focus:outline-none focus:border-white/20"
            />
          </div>
          <button
            onClick={() => {
              setSyncResult(null);
              syncMutation.mutate();
            }}
            disabled={syncMutation.isPending}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-500/20 border border-orange-500/30 text-orange-300 hover:bg-orange-500/30 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {syncMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            {syncMutation.isPending
              ? t("hubspotMapping.sync.syncing")
              : t("hubspotMapping.sync.button")}
          </button>
        </div>

        {/* Sync result summary */}
        {syncResult && (
          <div className="rounded-lg border border-white/[0.08] bg-black/20 p-3 space-y-1">
            <p className="text-[10px] font-mono text-white/40 uppercase tracking-wider mb-2">
              {t("hubspotMapping.result.title")}
            </p>
            <SyncResultRow
              label={t("hubspotMapping.result.scanned")}
              value={syncResult.dealsScanned}
            />
            <SyncResultRow
              label={t("hubspotMapping.result.created")}
              value={syncResult.conversionsCreated}
              accent="emerald"
            />
            <SyncResultRow
              label={t("hubspotMapping.result.skippedSynced")}
              value={syncResult.dealsSkippedAlreadySynced}
            />
            <SyncResultRow
              label={t("hubspotMapping.result.skippedNoMapping")}
              value={syncResult.dealsSkippedNoMapping}
            />
            <SyncResultRow
              label={t("hubspotMapping.result.skippedNoContact")}
              value={syncResult.dealsSkippedNoContact}
            />
            {syncResult.errors.length > 0 && (
              <div className="mt-2 pt-2 border-t border-white/[0.06]">
                <p className="text-[10px] text-red-400/80 font-mono">
                  {t("hubspotMapping.result.errorsCount", { count: syncResult.errors.length })}
                </p>
                {syncResult.errors.slice(0, 3).map((e, i) => (
                  <p key={i} className="text-[10px] text-red-400/60 font-mono truncate">
                    {e}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        {syncMutation.isError && (
          <div className="flex items-center gap-2 text-xs text-red-400/80 p-2 bg-red-500/10 rounded-lg border border-red-500/20">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
            {syncMutation.error?.message ?? t("hubspotMapping.sync.error")}
          </div>
        )}
      </div>
    </div>
  );
}

function SyncResultRow({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: "emerald";
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[11px] text-white/50">{label}</span>
      <span
        className={`text-[11px] font-mono font-medium ${accent === "emerald" ? "text-emerald-400" : "text-white/70"}`}
      >
        {value}
      </span>
    </div>
  );
}
